import { Schema } from "effect";
import { AgentActivityEventSchema, AgentActivitySnapshotSchema } from "./agent-events";
import {
  AgentStorageBoundaryParseOptions,
  DurableAgentStatusSchema,
  EncodedAgentMessageHistorySchema,
  EncodedAgentMessageSchema,
} from "./agent-storage";
import { toPapersFlowValidationSchema } from "./standard";

export const AGENT_RUN_ATTACHMENT_EVENT_PAGE_SIZE = 128;

const IdentifierSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(180));
const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const PositiveSafeIntegerSchema = NonNegativeSafeIntegerSchema.check(
  Schema.isGreaterThanOrEqualTo(1),
);

export const ReadAgentRunAttachmentRequestSchema = Schema.Struct({
  threadId: IdentifierSchema,
  expectedRunId: Schema.optionalKey(IdentifierSchema),
  fromSequence: NonNegativeSafeIntegerSchema,
  includeMessages: Schema.Boolean,
});

const IdleAgentRunAttachmentSchema = Schema.Struct({
  kind: Schema.Literal("idle"),
  threadId: IdentifierSchema,
});

const terminalStatuses = new Set(["succeeded", "failed", "cancelled"]);

export const AgentRunAttachmentPageSchema = Schema.Struct({
  kind: Schema.Literal("run"),
  workspaceId: IdentifierSchema,
  threadId: IdentifierSchema,
  runId: IdentifierSchema,
  status: DurableAgentStatusSchema,
  generation: PositiveSafeIntegerSchema,
  fromSequence: NonNegativeSafeIntegerSchema,
  nextSequence: NonNegativeSafeIntegerSchema,
  snapshotSequence: NonNegativeSafeIntegerSchema,
  hasMore: Schema.Boolean,
  snapshot: AgentActivitySnapshotSchema,
  events: Schema.Array(AgentActivityEventSchema).check(
    Schema.isMaxLength(AGENT_RUN_ATTACHMENT_EVENT_PAGE_SIZE),
  ),
  encodedMessages: Schema.optionalKey(EncodedAgentMessageHistorySchema),
  outputEncodedMessage: Schema.optionalKey(EncodedAgentMessageSchema),
}).check(
  Schema.makeFilter(
    (page) =>
      page.nextSequence === page.fromSequence + page.events.length ||
      "attachment event cursor must advance by the returned event count",
  ),
  Schema.makeFilter(
    (page) =>
      page.nextSequence <= page.snapshotSequence ||
      "attachment event cursor cannot exceed the durable snapshot sequence",
  ),
  Schema.makeFilter(
    (page) =>
      page.hasMore === page.nextSequence < page.snapshotSequence ||
      "attachment pagination must match the durable snapshot sequence",
  ),
  Schema.makeFilter(
    (page) =>
      (page.snapshot.runId === page.runId && page.snapshot.status === page.status) ||
      "attachment snapshot must describe the attached run",
  ),
  Schema.makeFilter(
    (page) =>
      page.events.every(
        (event, index) =>
          event.workspaceId === page.workspaceId &&
          event.runId === page.runId &&
          event.sequence === page.fromSequence + index,
      ) || "attachment events must be contiguous and scoped to the attached run",
  ),
  Schema.makeFilter(
    (page) =>
      page.outputEncodedMessage === undefined ||
      terminalStatuses.has(page.status) ||
      "only a terminal run may expose its durable output message",
  ),
  Schema.makeFilter(
    (page) =>
      page.status !== "succeeded" ||
      page.outputEncodedMessage !== undefined ||
      "a succeeded run must expose its durable output message",
  ),
);

export const ReadAgentRunAttachmentResultSchema = Schema.Union([
  IdleAgentRunAttachmentSchema,
  AgentRunAttachmentPageSchema,
]);

const AgentMessageReplayBaseFields = {
  version: Schema.Literal("agent-message-replay/v1"),
  threadId: IdentifierSchema,
  runId: IdentifierSchema,
  revision: NonNegativeSafeIntegerSchema,
} as const;

export const AgentMessageReplaySchema = Schema.Union([
  Schema.Struct({
    ...AgentMessageReplayBaseFields,
    mode: Schema.Literal("timeline"),
    encodedMessages: EncodedAgentMessageHistorySchema,
  }),
  Schema.Struct({
    ...AgentMessageReplayBaseFields,
    mode: Schema.Literal("final"),
    encodedMessages: Schema.Array(EncodedAgentMessageSchema).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(1),
    ),
  }),
]);

export const AgentMessageReplayStandardSchema =
  toPapersFlowValidationSchema(AgentMessageReplaySchema);

export const AgentAttachmentCursorUpdateSchema = Schema.Struct({
  version: Schema.Literal("agent-attachment-cursor/v1"),
  threadId: IdentifierSchema,
  runId: IdentifierSchema,
  nextSequence: NonNegativeSafeIntegerSchema,
});

export const AgentAttachmentCursorUpdateStandardSchema = toPapersFlowValidationSchema(
  AgentAttachmentCursorUpdateSchema,
);

export const decodeReadAgentRunAttachmentRequest = Schema.decodeUnknownSync(
  ReadAgentRunAttachmentRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeReadAgentRunAttachmentResult = Schema.decodeUnknownSync(
  ReadAgentRunAttachmentResultSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeAgentMessageReplayOption = Schema.decodeUnknownOption(
  AgentMessageReplaySchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeAgentAttachmentCursorUpdateOption = Schema.decodeUnknownOption(
  AgentAttachmentCursorUpdateSchema,
  AgentStorageBoundaryParseOptions,
);
export const encodeReadAgentRunAttachmentRequest = Schema.encodeSync(
  ReadAgentRunAttachmentRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const encodeReadAgentRunAttachmentResult = Schema.encodeSync(
  ReadAgentRunAttachmentResultSchema,
  AgentStorageBoundaryParseOptions,
);

export type ReadAgentRunAttachmentRequest = typeof ReadAgentRunAttachmentRequestSchema.Type;
export type ReadAgentRunAttachmentResult = typeof ReadAgentRunAttachmentResultSchema.Type;
export type AgentRunAttachmentPage = typeof AgentRunAttachmentPageSchema.Type;
export type AgentMessageReplay = typeof AgentMessageReplaySchema.Type;
export type AgentAttachmentCursorUpdate = typeof AgentAttachmentCursorUpdateSchema.Type;
